/**
 * Calculates the compounded return for a set of periodic returns.
 *
 * Formula: (∏(1 + r_i/100) - 1) × 100
 * Each return value is expected as a percentage (e.g. 1.64 means 1.64%).
 *
 * @param returns - Array of return values as percentages. Undefined/null entries are skipped.
 * @returns Compounded return as a percentage, rounded to 2 decimal places.
 *          Returns null if there are no valid return values.
 */
export function compoundReturns(returns: (number | undefined | null)[]): number | null {
    const valid = returns.filter((r): r is number => r !== null && r !== undefined && !Number.isNaN(r));
    if (valid.length === 0) return null;

    const compounded = valid.reduce((acc, r) => acc * (1 + r / 100), 1);
    return parseFloat(((compounded - 1) * 100).toFixed(2));
}

/**
 * Annualized return from monthly percentage returns.
 * Formula: (∏(1 + r_i/100))^(12/n) - 1, expressed as %.
 */
export function annualizedReturn(monthlyReturns: (number | undefined | null)[]): number | null {
    const valid = monthlyReturns.filter((r): r is number => r !== null && r !== undefined && !Number.isNaN(r));
    if (valid.length === 0) return null;

    const compounded = valid.reduce((acc, r) => acc * (1 + r / 100), 1);
    const annualized = Math.pow(compounded, 12 / valid.length) - 1;
    return parseFloat((annualized * 100).toFixed(2));
}

/**
 * Annualized volatility from monthly percentage returns.
 * Formula: std_dev(monthly returns) × √12.
 */
export function annualizedVolatility(monthlyReturns: (number | undefined | null)[]): number | null {
    const valid = monthlyReturns.filter((r): r is number => r !== null && r !== undefined && !Number.isNaN(r));
    if (valid.length < 2) return null;

    const mean = valid.reduce((sum, r) => sum + r, 0) / valid.length;
    const variance = valid.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / (valid.length - 1);
    const monthlyStdDev = Math.sqrt(variance);
    return parseFloat((monthlyStdDev * Math.sqrt(12)).toFixed(2));
}

/**
 * Sharpe Ratio from monthly percentage returns.
 * Formula: annualizedReturn / annualizedVolatility (risk-free rate assumed 0).
 */
export function sharpeRatio(monthlyReturns: (number | undefined | null)[]): number | null {
    const annRet = annualizedReturn(monthlyReturns);
    const annVol = annualizedVolatility(monthlyReturns);
    if (annRet === null || annVol === null || annVol === 0) return null;
    return parseFloat((annRet / annVol).toFixed(2));
}
